import {
	type FrontendPlacementMessage,
	type AnyFrontendStage,
} from '@post-office/message-maker-browser/types';

import { noValidation } from '@post-office/message-maker-browser/message-helpers';

import { PlacementFrontend } from '@post-office/browser--post-office-team/rovo-nudge';

import { NudgeDefinition } from '@post-office/browser--post-office-team/rovo-nudge/nudge-definition';

import AiGenerativeRemoveSilenceIcon from '@atlaskit/icon-lab/core/ai-generative-remove-silence';

import { useCallback } from 'react';

import { HandleActionType } from '@atlassian/rovo-conversation-actions/types';

import { useClickedRovoNudgeDismiss, useClickedRovoNudgeCta } from '@post-office/rovo-nudge-util';

import { defineMessages, useIntl } from 'react-intl';

import type { CreatePlacementMessageConfig } from '@post-office/message-template-contracts/placement-v2';

import type { MessageTemplateContext } from '../../../index';

const messages = defineMessages({
	title: {
		id: 'post-office.rovo-chat.jira-work-item-catchup-parent.title',
		defaultMessage: 'Catch up on this work item',
		description:
			'Title of the Rovo nudge that offers a summary of recent changes to a Jira work item and its child work items',
	},
	secondaryTitle: {
		id: 'post-office.rovo-chat.jira-work-item-catchup-parent.secondary-title',
		defaultMessage: 'See what changed since your last visit.',
		description:
			'Secondary title of the Rovo nudge that offers a summary of recent changes to a Jira work item',
	},
	description: {
		id: 'post-office.rovo-chat.jira-work-item-catchup-parent.description',
		defaultMessage: 'See what changed on this work item and its children since your last visit.',
		description:
			'Supporting text of the Rovo nudge that offers a summary of recent changes to a Jira work item and its child work items',
	},
	iconLabel: {
		id: 'post-office.rovo-chat.jira-work-item-catchup-parent.icon-label',
		defaultMessage: 'Work item catch-up',
		description: 'Accessible label for the icon of the Jira work item catch-up Rovo nudge',
	},
});

const nudgeContext = {
	id: 'jira-work-item-catchup-parent',
};

/**
 * The skill invocation sent to Rovo Chat. The leading `/work-item-catchup` is encoded as
 * a skill pill and the message is sent automatically.
 */
export const buildCatchupPrompt = ({
	parentKey,
	after,
}: Pick<MessageTemplateContext, 'parentKey' | 'after'>): string =>
	`/work-item-catchup parent=${parentKey} after=${after}`;

export const MessageFrontend: (config: CreatePlacementMessageConfig) => FrontendPlacementMessage<{
	render: AnyFrontendStage;
}> = PlacementFrontend.createMessageFrontend({
	render: {
		validate: noValidation<MessageTemplateContext>(),
		Component: ({ parentKey, after }: Partial<MessageTemplateContext>) => {
			const intl = useIntl();
			useClickedRovoNudgeCta({ nudgeId: nudgeContext.id });
			useClickedRovoNudgeDismiss({ nudgeId: nudgeContext.id });

			const iconLabel = intl.formatMessage(messages.iconLabel);
			const icon = useCallback(
				() => <AiGenerativeRemoveSilenceIcon size="medium" label={iconLabel} />,
				[iconLabel],
			);

			// Data that matches no requested fragment arrives without these fields.
			if (!parentKey || !after) {
				return null;
			}

			// No `onClick`: the action trigger only publishes the skill prompt when the
			// action has no custom click handler.
			return (
				<NudgeDefinition
					type="skill-action"
					title={intl.formatMessage(messages.title)}
					secondaryTitle={intl.formatMessage(messages.secondaryTitle)}
					description={intl.formatMessage(messages.description)}
					icon={icon}
					handleType={HandleActionType.Skill}
					staticPrompt={buildCatchupPrompt({ parentKey, after })}
					starterType="static"
					nudgeContext={nudgeContext}
				/>
			);
		},
	},
});
